/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Form } from '@unform/web';
import { FormHandles } from '@unform/core';
import Swal from 'sweetalert2';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

import api from '~/services/api';
import {
  useBulkEmailDispatch,
  IDispatchRecord,
} from '~/hooks/useBulkEmailDispatch';
import Select, { IOption } from '~/components/Select';
import { KrClose } from '~/components/KoroIcons';

import {
  Container,
  FormWrapper,
  ProgressWrapper,
  ResultCard,
  ErrorCard,
  DispatchesTable,
  Modal,
} from './styles';
import Input from '~/components/Input';

interface IEventOption {
  id: number;
  title: string;
}

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  queued: { label: 'Na fila', color: '#ff6900' },
  sent: { label: 'Enviado', color: '#00b15c' },
  failed: { label: 'Falhou', color: '#dc3545' },
  opened: { label: 'Aberto', color: '#0d6efd' },
};

const BulkEmails: React.FC = () => {
  const formRef = useRef<FormHandles>(null);
  const [events, setEvents] = useState<IEventOption[]>([]);
  const [selectedEventId, setSelectedEventId] = useState<number | null>(null);
  const [selectedEventName, setSelectedEventName] = useState('');
  const [emailType, setEmailType] = useState('reminder_7days');
  const { state, dispatch, cancel, fetchDispatches } = useBulkEmailDispatch();
  const [eventOptionsData, setEventOptionsData] = useState<IOption[]>([]);
  const [eventSearchText, setEventSearchText] = useState('');
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [showCustomDataModal, setShowCustomDataModal] = useState(false);
  const [deliveryKitsInfo, setDeliveryKitsInfo] = useState('');
  const [customData, setCustomData] = useState('');
  const [subject, setSubject] = useState('');

  const { isLoading, progress, result, dispatches } = state;

  useEffect(() => {
    async function loadEvents() {
      try {
        const response = await api.get('events/admin');
        const data = response.data.data || response.data;
        setEvents(
          data.map((event: any) => ({
            id: event.id,
            title: event.name || event.title,
          }))
        );
      } catch (error) {
        console.error('Erro ao carregar eventos', error);
      }
    }

    loadEvents();
  }, []);

  const eventOptions: IOption[] = useMemo(
    () => [
      {
        id: '',
        value: 'Selecione um evento',
        selected: !selectedEventId,
        notSelectable: true,
      },
      ...events.map((event) => ({
        id: event.id,
        value: event.title,
        selected: selectedEventId === event.id,
      })),
    ],
    [events, selectedEventId]
  );

  const emailTypeOptions: IOption[] = useMemo(
    () => [
      {
        id: 'reminder_7days',
        value: 'Lembrete 7 dias',
        selected: emailType === 'reminder_7days',
      },
      {
        id: 'reminder_6days',
        value: 'Lembrete 6 dias',
        selected: emailType === 'reminder_6days',
      },
      {
        id: 'reminder_5days',
        value: 'Lembrete 5 dias',
        selected: emailType === 'reminder_5days',
      },
      {
        id: 'reminder_4days',
        value: 'Lembrete 4 dias',
        selected: emailType === 'reminder_4days',
      },
      {
        id: 'reminder_3days',
        value: 'Lembrete 3 dias',
        selected: emailType === 'reminder_3days',
      },
      {
        id: 'reminder_2days',
        value: 'Lembrete 2 dias',
        selected: emailType === 'reminder_2days',
      },
      {
        id: 'reminder_1day',
        value: 'Lembrete 1 dia',
        selected: emailType === 'reminder_1day',
      },
      {
        id: 'kit_delivery',
        value: 'Entrega de Kits',
        selected: emailType === 'kit_delivery',
      },
      {
        id: 'custom',
        value: 'Personalizado',
        selected: emailType === 'custom',
      },
    ],
    [emailType]
  );

  const isDeliveryInfoEmpty = useMemo(
    () =>
      !deliveryKitsInfo ||
      deliveryKitsInfo.replace(/<(.|\n)*?>/g, '').trim().length === 0,
    [deliveryKitsInfo]
  );

  const isCustomDataEmpty = useMemo(
    () =>
      !customData || customData.replace(/<(.|\n)*?>/g, '').trim().length === 0,
    [customData]
  );

  const handleDispatch = useCallback(() => {
    if (!selectedEventId) return;

    if (emailType === 'kit_delivery' && isDeliveryInfoEmpty) {
      setShowDeliveryModal(true);
      return;
    }

    if (emailType === 'custom' && isCustomDataEmpty) {
      setShowCustomDataModal(true);
      return;
    }

    Swal.fire({
      title: 'Confirmar disparo',
      text: 'Deseja realmente disparar os emails para todos os participantes deste evento?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ff6900',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Sim, disparar!',
      cancelButtonText: 'Cancelar',
      reverseButtons: true,
    }).then((swalResult) => {
      if (swalResult.isConfirmed) {
        dispatch(
          selectedEventId,
          emailType,
          selectedEventName,
          emailType === 'kit_delivery' ? deliveryKitsInfo : undefined,
          emailType === 'custom' ? { subject, htmlBody: customData } : undefined
        );
      }
    });
  }, [
    selectedEventId,
    emailType,
    isDeliveryInfoEmpty,
    isCustomDataEmpty,
    dispatch,
    selectedEventName,
    deliveryKitsInfo,
    subject,
    customData,
  ]);

  const percentage =
    progress?.total && progress?.current
      ? Math.round((progress.current / progress.total) * 100)
      : 0;

  const handleChangeText = useCallback(
    (text: string) => {
      setEventSearchText(text);
      if (text) {
        setEventOptionsData(
          eventOptions
            .filter(
              (option) =>
                !option.notSelectable &&
                option.value.toLowerCase().includes(text.toLowerCase())
            )
            .map((option) => ({ ...option, selected: false }))
        );
      } else {
        setEventOptionsData([]);
      }
    },
    [eventOptions]
  );

  const [loadingDispatches, setLoadingDispatches] = useState(false);

  const handleLoadDispatches = useCallback(async () => {
    if (selectedEventId) {
      setLoadingDispatches(true);
      await fetchDispatches(selectedEventId);
      setLoadingDispatches(false);
    }
  }, [selectedEventId, fetchDispatches]);

  return (
    <Container>
      <div className="container-fluid py-4">
        <h1 className="mb-4">Disparo de Emails em Massa</h1>

        <div className="bg-white p-4">
          <Form ref={formRef} onSubmit={() => undefined}>
            <FormWrapper>
              <div className="row">
                <div className="col-md-5 mb-3">
                  <label>Evento</label>
                  <Select
                    name="event"
                    className="bd-select mt-0"
                    options={
                      eventOptionsData.length > 0
                        ? eventOptionsData
                        : eventOptions
                    }
                    onChange={(option: any) => {
                      setSelectedEventId(option.id ? Number(option.id) : null);
                      setSelectedEventName(option.value || '');
                      setEventSearchText('');
                      setEventOptionsData([]);
                    }}
                    readOnly={isLoading}
                    onChangeText={handleChangeText}
                    valueSelected={eventSearchText}
                  />
                </div>

                <div className="col-md-4 mb-3">
                  <label>Tipo de disparo</label>
                  <Select
                    name="emailType"
                    className="bd-select mt-0"
                    options={emailTypeOptions}
                    onChange={(option: any) => {
                      const newType = String(option.id);
                      setEmailType(newType);
                      if (newType === 'kit_delivery') {
                        setShowDeliveryModal(true);
                      }
                      if (newType === 'custom') {
                        setShowCustomDataModal(true);
                      }
                    }}
                    readOnly={isLoading}
                  />
                  {emailType === 'kit_delivery' && (
                    <button
                      type="button"
                      className="btn-edit-delivery-info mt-2"
                      onClick={() => setShowDeliveryModal(true)}
                    >
                      {isDeliveryInfoEmpty
                        ? '+ Adicionar informações da entrega'
                        : '✎ Editar informações da entrega'}
                    </button>
                  )}
                  {emailType === 'custom' && (
                    <button
                      type="button"
                      className="btn-edit-delivery-info mt-2"
                      onClick={() => setShowCustomDataModal(true)}
                    >
                      {isCustomDataEmpty
                        ? '+ Adicionar dados customizados'
                        : '✎ Editar dados customizados'}
                    </button>
                  )}
                </div>

                <div className="col-md-3 mb-3 d-flex align-items-end gap-2">
                  <button
                    type="button"
                    className="btn-dispatch"
                    onClick={handleDispatch}
                    disabled={isLoading || !selectedEventId}
                  >
                    {isLoading ? 'Enviando...' : 'Disparar Emails'}
                  </button>
                  {isLoading && (
                    <button
                      type="button"
                      className="btn-cancel"
                      onClick={cancel}
                    >
                      Cancelar
                    </button>
                  )}
                </div>
              </div>
            </FormWrapper>
          </Form>

          {isLoading && progress && (
            <ProgressWrapper className="mt-4">
              <div className="progress-bar-container">
                <div
                  className="progress-bar-fill"
                  style={{ width: `${percentage}%` }}
                />
              </div>
              <div className="progress-info">
                <span>
                  {progress.current || 0}/{progress.total} processados
                </span>
                {' — '}
                <span className="sent">{progress.sent || 0} enviados</span>
                {' | '}
                <span className="failed">{progress.failed || 0} falhas</span>
              </div>
              {progress.ticket_email && (
                <div className="progress-email">
                  Último: {progress.ticket_email} ({progress.ticket_status})
                </div>
              )}
            </ProgressWrapper>
          )}

          {result?.event === 'done' && (
            <ResultCard className="mt-4">
              <h3>Disparo finalizado!</h3>
              <div className="result-stats">
                <p>Total: {result.total}</p>
                <p className="sent">Enviados: {result.sent}</p>
                <p className="failed">Falhas: {result.failed}</p>
              </div>
              {result.errors && result.errors.length > 0 && (
                <div className="errors-list">
                  <details>
                    <summary>Ver erros ({result.errors.length})</summary>
                    <ul>
                      {result.errors.map((err, i) => (
                        <li key={i}>{err}</li>
                      ))}
                    </ul>
                  </details>
                </div>
              )}
            </ResultCard>
          )}

          {result?.event === 'error' && (
            <ErrorCard className="mt-4">{result.message}</ErrorCard>
          )}
        </div>

        {/* Tabela de Status dos Disparos */}
        {selectedEventId && (
          <div className="bg-white p-4 mt-4">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <h2 className="mb-0">Status dos Disparos</h2>
              <button
                type="button"
                className="btn-refresh"
                onClick={handleLoadDispatches}
                disabled={loadingDispatches}
              >
                {loadingDispatches ? 'Atualizando...' : 'Atualizar'}
              </button>
            </div>

            {dispatches.length > 0 ? (
              <DispatchesTable>
                <table>
                  <thead>
                    <tr>
                      <th>Destinatário</th>
                      <th>Email</th>
                      <th>Tipo</th>
                      <th>Status</th>
                      <th>Aberturas</th>
                      <th>Data</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dispatches.map((d: IDispatchRecord) => (
                      <tr key={d.id}>
                        <td>{d.name}</td>
                        <td>{d.email}</td>
                        <td>{d.type}</td>
                        <td>
                          <span
                            className="status-badge"
                            style={{
                              background: `${STATUS_LABELS[d.status]?.color}15`,
                              color: STATUS_LABELS[d.status]?.color,
                            }}
                          >
                            {STATUS_LABELS[d.status]?.label || d.status}
                          </span>
                        </td>
                        <td>{d.open_count}</td>
                        <td>
                          {new Date(d.created_at).toLocaleString('pt-BR')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </DispatchesTable>
            ) : (
              <p className="text-muted">
                Nenhum disparo registrado para este evento.
              </p>
            )}
          </div>
        )}
      </div>

      <Modal
        className="zoom modal-filter"
        size="lg"
        centered
        show={showDeliveryModal}
        onHide={() => setShowDeliveryModal(false)}
      >
        <Modal.Header className="justify-content-between align-items-center border-0 px-3 px-sm-5 pb-0 pt-0">
          <Modal.Title className="mb-0 mt-4">
            <h2 className="title mb-0">Informações da entrega dos kits</h2>
            <p className="modal-subtitle mb-0 mt-1">
              Escreva a data e demais detalhes da entrega dos kits. Este
              conteúdo será incluído no email enviado aos participantes.
            </p>
          </Modal.Title>
          <button
            type="button"
            onClick={() => setShowDeliveryModal(false)}
            className="border-0 bg-transparent"
          >
            <KrClose size={20} color="#989898" />
          </button>
        </Modal.Header>
        <Modal.Body className="container pt-0 px-3 px-sm-5">
          <div className="row">
            <div className="col-12">
              <hr className="mb-4" />
              <ReactQuill
                theme="snow"
                value={deliveryKitsInfo}
                onChange={setDeliveryKitsInfo}
                placeholder="Ex.: A entrega dos kits será realizada no dia 10/08/2026, das 9h às 18h, no endereço..."
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="border-0 py-4 px-5">
          <button
            type="button"
            className="btn-cancel-modal"
            onClick={() => setShowDeliveryModal(false)}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="btn-save"
            disabled={isDeliveryInfoEmpty}
            onClick={() => setShowDeliveryModal(false)}
          >
            Salvar
          </button>
        </Modal.Footer>
      </Modal>

      <Modal
        className="zoom modal-filter"
        size="lg"
        centered
        show={showCustomDataModal}
        onHide={() => setShowCustomDataModal(false)}
      >
        <Modal.Header className="justify-content-between align-items-center border-0 px-3 px-sm-5 pb-0 pt-0">
          <Modal.Title className="mb-0 mt-4">
            <h2 className="title mb-0">
              Informações do personalizado do e-mail
            </h2>
            <p className="modal-subtitle mb-0 mt-1">
              Escreva os demais detalhes do personalizado do e-mail. Este
              conteúdo será incluído no email enviado aos participantes.
            </p>
          </Modal.Title>
          <button
            type="button"
            onClick={() => setShowCustomDataModal(false)}
            className="border-0 bg-transparent"
          >
            <KrClose size={20} color="#989898" />
          </button>
        </Modal.Header>
        <Modal.Body className="container pt-0 px-3 px-sm-5">
          <div className="row">
            <div className="col-12">
              <label className="form-label">Assunto do e-mail</label>
              <input
                type="text"
                name="subject"
                placeholder="Ex.: Assunto do e-mail"
                className="form-control"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
              <hr className="mb-4" />
              <ReactQuill
                theme="snow"
                value={customData}
                onChange={setCustomData}
                placeholder="Ex.: O personalizado do e-mail incluirá informações adicionais sobre o evento..."
              />
              {console.log(customData)}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="border-0 py-4 px-5">
          <button
            type="button"
            className="btn-cancel-modal"
            onClick={() => setShowCustomDataModal(false)}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="btn-save"
            disabled={isCustomDataEmpty}
            onClick={() => setShowCustomDataModal(false)}
          >
            Salvar
          </button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default BulkEmails;
